import { Injectable } from '@angular/core';

import { AbstractSecurityStorage } from 'angular-auth-oidc-client';

import { TabSyncService } from './tab-sync.service';

@Injectable({
  providedIn: 'root',
})
export class OidcSynchronizedStorageService implements AbstractSecurityStorage {
  private readonly oidcKeys = [
    'authWellKnownEndPoints',
    'authStateControl',
    'authNonce',
    'authCodeVerifier',
    'authResult',
    'refresh_token',
    'access_token',
    'id_token',
    'userData',
    'authnResult',
    'session_state',
    'storageSilentRenewRunning',
    'storageCodeFlowInProgress',
  ];

  private silentRenewInProgress = false;

  constructor(private tabSyncService: TabSyncService) {
    this.initializeTokenListener();
  }

  private initializeTokenListener(): void {
    // Followers just listen for token update notifications
    // No need to write to localStorage - it's already shared across tabs
    this.tabSyncService.tokenUpdate$.subscribe(() => {
      // This notification can trigger UI updates or event listeners
      // The actual token is already in localStorage (written by leader)
    });
  }

  read(key: string): string | null {
    // Block non-leaders from seeing silent renew running flag to prevent them from starting refresh
    if (!this.tabSyncService.isLeader() && this.isSilentRenewRunningKey(key)) {
      const value = localStorage.getItem(key);
      // If silent renew is running (set by leader), return 'true' to prevent this tab from starting
      if (value === 'true') {
        return 'true';
      }
      // Return 'false' to allow only the leader to start silent renew
      return 'false';
    }

    return localStorage.getItem(key);
  }

  write(key: string, value: string): void {
    const isOidcKey = this.isOidcRelatedKey(key);

    // Critical: Block non-leaders from initiating token refresh flows
    if (!this.tabSyncService.isLeader()) {
      if (this.isSilentRenewRunningKey(key)) {
        // Silently ignore - non-leaders cannot start silent renew
        console.debug(`[Tab-Sync] Non-leader blocked from setting ${key}`);
        return;
      }
      // For storageCodeFlowInProgress: Allow during initial auth (user-triggered login)
      // but block for automatic flows. The OIDC library handles deduplication.
      if (this.isCodeFlowInProgressKey(key) && localStorage.getItem('access_token')) {
        // If we already have tokens, block non-leaders from starting new auth flows
        console.debug(`[Tab-Sync] Non-leader blocked from setting ${key}`);
        return;
      }
    }

    // Track silent renew state
    if (this.isSilentRenewRunningKey(key)) {
      this.silentRenewInProgress = value === 'true';
    }

    // Leader and non-OIDC keys: write normally
    localStorage.setItem(key, value);

    // Leader broadcasts OIDC updates to followers
    if (isOidcKey && this.tabSyncService.isLeader()) {
      this.tabSyncService.broadcastTokenUpdate(key, value);
    }
  }

  remove(key: string): void {
    const isOidcKey = this.isOidcRelatedKey(key);

    if (isOidcKey && !this.tabSyncService.isLeader()) {
      // Followers don't remove OIDC keys
      if (this.isSilentRenewRunningKey(key) || this.isCodeFlowInProgressKey(key)) {
        return; // Block followers from clearing flow flags
      }
    }

    // Leader and non-OIDC keys: remove normally
    localStorage.removeItem(key);

    // Leader broadcasts OIDC removals to followers
    if (isOidcKey && this.tabSyncService.isLeader()) {
      this.tabSyncService.broadcastTokenUpdate(key, null);
    }
  }

  clear(): void {
    // Only the leader can clear storage
    if (!this.tabSyncService.isLeader()) {
      return; // Followers should not clear storage
    }

    const keysToSync: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && this.isOidcRelatedKey(key)) {
        keysToSync.push(key);
      }
    }

    localStorage.clear();

    // Notify followers that storage was cleared
    keysToSync.forEach((key) => {
      this.tabSyncService.broadcastTokenUpdate(key, null);
    });
  }

  private isOidcRelatedKey(key: string): boolean {
    return (
      this.oidcKeys.some((oidcKey) => key.includes(oidcKey)) ||
      key.startsWith('oidc.') ||
      key.includes('token') ||
      key.includes('auth')
    );
  }

  private isSilentRenewRunningKey(key: string): boolean {
    return key.includes('storageSilentRenewRunning');
  }

  private isCodeFlowInProgressKey(key: string): boolean {
    return key.includes('storageCodeFlowInProgress');
  }

  public isRenewInProgress(): boolean {
    return this.silentRenewInProgress;
  }
}
